import { useState, useCallback, useEffect } from "react";
import type { VideoSpeed } from "../types/video";

export function useVideoState(videoRef: React.RefObject<HTMLVideoElement>) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [speed, setSpeed] = useState<VideoSpeed>(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      setDuration(videoRef.current.duration);
    }
  }, [videoRef]);

  const togglePlay = useCallback(async () => {
    if (!videoRef.current) return;

    try {
      if (isPlaying) {
        await videoRef.current.pause();
      } else {
        await videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      console.error("Error toggling play state:", error);
    }
  }, [isPlaying]);

  const handleSeek = useCallback(
    (time: number) => {
      if (videoRef.current) {
        const newTime = Math.max(0, Math.min(time, duration));
        videoRef.current.currentTime = newTime;
        setCurrentTime(newTime);
      }
    },
    [duration]
  );

  const handleVolumeChange = useCallback((newVolume: number) => {
    if (videoRef.current) {
      const clampedVolume = Math.max(0, Math.min(newVolume, 1));
      videoRef.current.volume = clampedVolume;
      setVolume(clampedVolume);
      console.log("🔊 Ses seviyesi değişti:", Math.round(clampedVolume * 100) + "%");
    }
  }, []);

  const handleSpeedChange = useCallback((newSpeed: VideoSpeed) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = newSpeed;
      setSpeed(newSpeed);
      console.log("⚡ Oynatma hızı değişti:", newSpeed + "x");
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    const container = videoRef.current?.parentElement;
    if (!container) return;

    try {
      if (!document.fullscreenElement) {
        container.requestFullscreen();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error("Error toggling fullscreen:", error);
    }
  }, []);

  const seekBackward = useCallback(() => {
    if (videoRef.current) {
      const newTime = Math.max(0, currentTime - 10);
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, [currentTime]);

  const seekForward = useCallback(() => {
    if (videoRef.current) {
      const newTime = Math.min(duration, currentTime + 10);
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, [currentTime, duration]);

  const cleanup = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      videoRef.current.playbackRate = 1;
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setSpeed(1);
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Set initial values
    video.volume = volume;
    video.playbackRate = speed;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      // Ensure volume and speed are set after metadata loads
      video.volume = volume;
      video.playbackRate = speed;
    };
    const handleVolumeChange = () => {
      setVolume(video.volume);
    };
    const handleRateChange = () => {
      setSpeed(video.playbackRate as VideoSpeed);
    };

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("volumechange", handleVolumeChange);
    video.addEventListener("ratechange", handleRateChange);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("volumechange", handleVolumeChange);
      video.removeEventListener("ratechange", handleRateChange);
    };
  }, [volume, speed]);

  return {
    isPlaying,
    currentTime,
    duration,
    volume,
    speed,
    isFullscreen,
    togglePlay,
    handleTimeUpdate,
    handleSeek,
    handleVolumeChange,
    handleSpeedChange,
    toggleFullscreen,
    seekBackward,
    seekForward,
    cleanup,
  };
}
